import React from 'react';
import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { loadGoogleMapsApi } from '../../utils/googleMaps';
import { MapPin, Clock, Truck, Star, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

export default function FloristsSurryHills() {
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapError, setMapError] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const initMap = async () => {
      try {
        await loadGoogleMapsApi();
        
        if (mapRef.current && window.google) {
          const map = new google.maps.Map(mapRef.current, {
            center: { lat: -33.8886, lng: 151.2094 }, // Surry Hills coordinates
            zoom: 14,
            styles: [
              {
                featureType: "poi",
                elementType: "labels",
                stylers: [{ visibility: "off" }]
              }
            ]
          });

          // Add marker for Surry Hills
          new google.maps.Marker({
            position: { lat: -33.8886, lng: 151.2094 },
            map: map,
            title: "Surry Hills",
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: "brand-primary",
              fillOpacity: 0.8,
              strokeColor: "#fff",
              strokeWeight: 2
            }
          });
        }
      } catch (error) {
        console.error('Error loading map:', error);
        setMapError(true);
      }
    };

    initMap();
  }, []);

  const surryHillsSearchUrl = "/search?location=Surry+Hills+NSW+2010%2C+Australia&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.8886&lng=151.2094";

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Do Surry Hills florists offer same-day delivery?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, many Surry Hills-based florists offer same-day delivery when orders are placed before 2pm on weekdays."
        }
      },
      {
        "@type": "Question",
        "name": "How much does flower delivery in Surry Hills cost?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Delivery costs typically range from $7 to $16, depending on distance, florist, and delivery time."
        }
      },
      {
        "@type": "Question",
        "name": "What areas do Surry Hills florists deliver to?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Most florists in Surry Hills deliver to surrounding suburbs including Darlinghurst, Redfern, Chippendale, and Paddington."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Florists Surry Hills | Same-Day Flower Delivery | MarketBloom</title>
        <meta name="description" content="Browse the top florists in Surry Hills NSW offering same-day delivery. From artistic arrangements to trendy designs, MarketBloom connects you with Sydney's most creative florists." />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      
      <div className="pt-20 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="bg-white border-b">
          <div className="max-w-4xl mx-auto px-6 py-8">
            <div className="flex items-center gap-3 text-sm text-black mb-4">
              <span className="bg-[#967F81] text-white px-3 py-1 rounded-full font-medium">Local Guides</span>
              <span>•</span>
              <span>15 January 2025</span>
              <span>•</span>
              <span>5 min read</span>
            </div>
            <h1 className="scroll-m-20 text-4xl font-extrabold tracking-tight text-black lg:text-5xl">
              Best Florists in Surry Hills for Same-Day Delivery 2025
            </h1>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-6 py-12">
          
          {/* Hero Image */}
          <div className="mb-12">
            <img 
              src="/images/blog/best-florists-surry-hills-same-day-hero.jpg" 
              alt="Artistic floral display at a trendy Surry Hills florist on Crown Street"
              className="w-full h-80 object-cover rounded-lg shadow-lg"
            />
          </div>

          {/* Introduction */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <p className="text-xl leading-7 text-black mb-6">
              I think you'll agree with me when I say:
            </p>
            <p className="leading-7 text-black mb-6">
              Finding authentic local florists in Surry Hills NSW who deliver same-day shouldn't mean settling for overpriced corporate chains or generic supermarket bouquets.
            </p>
            <p className="leading-7 text-black mb-6">
              That's why MarketBloom exclusively partners with independent Surry Hills flower shops - the creative artisans nestled between Crown Street's boutiques and the galleries near Central Station who craft artistic arrangements that capture our suburb's trendy, cultural spirit.
            </p>
            <p className="leading-7 text-black">
              Whether you need urgent flower delivery to a Crown Street apartment, artistic arrangements for a gallery opening, or celebration flowers delivered to a heritage terrace in Chippendale, our network of local florists ensures fresh, beautiful blooms arrive on time - often within hours of ordering.
            </p>
          </div>

          {/* Quick Action CTA */}
          <div className="bg-[#967F81] text-white rounded-lg p-6 mb-12 text-center">
            <h2 className="text-2xl font-bold mb-3">Need Flowers Today?</h2>
            <p className="text-lg mb-4 opacity-90">Browse Surry Hills' finest florists offering same-day delivery options</p>
            <Link 
              to={`${surryHillsSearchUrl}&utm_source=seo&utm_medium=landing&utm_campaign=surry-hills-florists`}
              className="bg-white text-brand-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center gap-2 min-h-[48px]"
            >
              Shop Surry Hills Florists
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {/* What Makes Surry Hills Florists Special */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">What Makes Surry Hills Florists Special</h2>
            
            <p className="leading-7 text-black mb-6">
              Surry Hills' independent florists are as creative as the suburb itself. From the established flower shops near Central Station to the boutique studios scattered along Crown Street, these local artisans understand what inner-city residents love - artistic flair, sustainable practices, and designs that complement both heritage terraces and converted warehouse apartments.
            </p>
            <p className="leading-7 text-black mb-6">
              Unlike generic flower delivery services that ship from distant warehouses, Surry Hills florists source from Sydney Flower Market daily, ensuring peak freshness. They know that a bouquet delivered to a Crown Street gallery needs to be Instagram-worthy, and arrangements for Surry Hills Markets events should be as vibrant as the community.
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-4">
                <div className="bg-[#967F81]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="w-8 h-8 text-brand-primary" aria-label="Icon for artistic designs" />
                </div>
                <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Artistic Flair</h3>
                <p className="text-black">From gallery-worthy to trendy designs</p>
              </div>
              <div className="text-center p-4">
                <div className="bg-[#967F81]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-brand-primary" aria-label="Icon for inner-city knowledge" />
                </div>
                <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Inner-City Expertise</h3>
                <p className="text-black">Know every laneway and terrace</p>
              </div>
              <div className="text-center p-4">
                <div className="bg-[#967F81]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-brand-primary" aria-label="Icon for same-day service" />
                </div>
                <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Same-Day Service</h3>
                <p className="text-black">Available from multiple local florists</p>
              </div>
            </div>
          </div>

          {/* Same-Day Delivery Coverage */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Same-Day Delivery Coverage</h2>
            
            <p className="leading-7 text-black mb-6">
              Many of Surry Hills' best florists offer same-day delivery with varying cutoff times throughout the day. This service covers not just Surry Hills (2010) but often extends to neighboring inner-city suburbs.
            </p>

            <div className="mb-6">
              <div 
                ref={mapRef} 
                className="w-full h-80 rounded-lg shadow-md bg-gray-200"
              >
                {mapError && (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <MapPin className="w-8 h-8 mr-2" />
                    <span>Map unavailable - Surry Hills NSW 2010</span>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">Surry Hills</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">Darlinghurst</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">Redfern</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">Chippendale</span>
              </div>
            </div>
          </div>

          {/* Popular Flower Styles */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Popular Flower Styles in Surry Hills</h2>
            
            <p className="leading-7 text-black mb-6">
              The best Surry Hills florists understand their creative clientele perfectly. Young professionals in converted warehouses prefer edgy, contemporary arrangements that complement industrial interiors. Artists and gallery owners love avant-garde designs that make bold statements. And the heritage terrace residents seek sophisticated arrangements that honor the suburb's historic charm while embracing modern trends.
            </p>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="scroll-m-20 text-2xl font-semibold tracking-tight text-black mb-4">Most Requested Styles:</h3>
              <ul className="my-6 ml-6 list-disc [&>li]:mt-2">
                <li className="text-black">Contemporary arrangements with unique color combinations</li>
                <li className="text-black">Industrial-chic designs for warehouse apartments</li>
                <li className="text-black">Artistic arrangements perfect for gallery spaces</li>
                <li className="text-black">Heritage-inspired bouquets with modern twists</li>
              </ul>
            </div>
          </div>

          {/* Why Choose MarketBloom for Surry Hills Flower Delivery */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Why MarketBloom Champions Local Surry Hills Florists</h2>
            
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="bg-[#967F81] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">1</div>
                <div>
                  <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Supporting Inner-City Creative Businesses</h3>
                  <p className="leading-7 text-black">When you order through MarketBloom, 100% of your flower purchase goes directly to independent Surry Hills florists. Unlike corporate chains that take hefty commissions, we believe in empowering local artisans who know every corner from Crown Street to Central Station.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="bg-[#967F81] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">2</div>
                <div>
                  <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Real-Time Availability from Multiple Florists</h3>
                  <p className="leading-7 text-black">Our platform connects you with several Surry Hills flower shops simultaneously, showing live inventory and same-day delivery slots. This means better selection for last-minute orders and competitive pricing from florists competing for your business.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="bg-[#967F81] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">3</div>
                <div>
                  <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Transparent Reviews from Real Surry Hills Customers</h3>
                  <p className="leading-7 text-black">Every florist review on MarketBloom comes from verified local deliveries. Read honest feedback from your Surry Hills neighbours about flower quality, delivery timing, and customer service before choosing your florist.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="bg-[#967F81] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">4</div>
                <div>
                  <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Fast Inner-City Delivery Network</h3>
                  <p className="leading-7 text-black">Shorter delivery distances mean fresher flowers and faster service. Our Surry Hills florists often deliver by bicycle or on foot, supporting the suburb's eco-friendly ethos while ensuring your bouquet arrives in perfect condition.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="bg-[#967F81] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">5</div>
                <div>
                  <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Preserving Surry Hills' Creative Culture</h3>
                  <p className="leading-7 text-black">By choosing local florists over mass-market alternatives, you're helping maintain the artistic character that makes Surry Hills special. These artisan florists are part of the creative community that defines our suburb's cultural identity.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Internal Linking Section */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Explore Florists in Other Sydney Suburbs</h2>
            
            <p className="leading-7 text-black mb-6">
              Looking for flowers elsewhere in Sydney? MarketBloom connects you with the best local florists across the city:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/search?location=Newtown+NSW+2042&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.8978149&lng=151.1785003&occasions=Birthday&occasions=Anniversary&occasions=Wedding&occasions=Sympathy&occasions=Get+Well&occasions=Congratulations&occasions=Thank+You&occasions=New+Baby&occasions=Valentine%27s+Day&occasions=Mother%27s+Day&occasions=Christmas&occasions=Graduation&occasions=Housewarming&occasions=Corporate&categories=Bouquet&categories=Floral+Arrangement&categories=Potted+Plant&categories=Single+Flower&categories=Other" className="text-brand-primary hover:underline">
                • Newtown florists - bohemian blooms and creative arrangements
              </Link>
              <Link to="/search?location=Bondi+NSW+2026&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.8915&lng=151.2767&occasions=Birthday&occasions=Anniversary&occasions=Wedding&occasions=Sympathy&occasions=Get+Well&occasions=Congratulations&occasions=Thank+You&occasions=New+Baby&occasions=Valentine%27s+Day&occasions=Mother%27s+Day&occasions=Christmas&occasions=Graduation&occasions=Housewarming&occasions=Corporate&categories=Bouquet&categories=Floral+Arrangement&categories=Potted+Plant&categories=Single+Flower&categories=Other" className="text-brand-primary hover:underline">
                • Bondi florists - beachside blooms and coastal arrangements
              </Link>
              <Link to="/search?location=Parramatta+NSW+2150&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.8175438&lng=151.0000257&occasions=Birthday&occasions=Anniversary&occasions=Wedding&occasions=Sympathy&occasions=Get+Well&occasions=Congratulations&occasions=Thank+You&occasions=New+Baby&occasions=Valentine%27s+Day&occasions=Mother%27s+Day&occasions=Christmas&occasions=Graduation&occasions=Housewarming&occasions=Corporate&categories=Bouquet&categories=Floral+Arrangement&categories=Potted+Plant&categories=Single+Flower&categories=Other" className="text-brand-primary hover:underline">
                • Parramatta flower delivery - Western Sydney's finest
              </Link>
              <Link to="/search?location=Marrickville+NSW+2204&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.9113438&lng=151.1553063&occasions=Birthday&occasions=Anniversary&occasions=Wedding&occasions=Sympathy&occasions=Get+Well&occasions=Congratulations&occasions=Thank+You&occasions=New+Baby&occasions=Valentine%27s+Day&occasions=Mother%27s+Day&occasions=Christmas&occasions=Graduation&occasions=Housewarming&occasions=Corporate&categories=Bouquet&categories=Floral+Arrangement&categories=Potted+Plant&categories=Single+Flower&categories=Other" className="text-brand-primary hover:underline">
                • Marrickville florists - multicultural floral artistry
              </Link>
            </div>
          </div>

          {/* Testimonial */}
          <div className="bg-neutral-50 rounded-lg px-6 py-5 mb-8 shadow-sm">
            <blockquote className="italic text-black">
              <p className="leading-7 mb-2">
                "MarketBloom helped me send artistic flowers to a Crown Street gallery opening within 4 hours — the contemporary arrangement was absolutely perfect for the space!"
              </p>
              <footer className="text-sm font-medium not-italic">
                – Alex R., Surry Hills
              </footer>
            </blockquote>
          </div>

          {/* FAQ Section */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Frequently Asked Questions</h2>
            
            <div className="space-y-4">
              <div className="border-b pb-4">
                <button
                  onClick={() => setOpenFaq(openFaq === 0 ? null : 0)}
                  className="flex justify-between items-center w-full text-left p-2 hover:bg-gray-50 rounded transition-colors min-h-[48px]"
                  aria-expanded={openFaq === 0}
                  aria-controls="faq-answer-0"
                >
                  <h3 className="text-xl font-semibold text-black">Do Surry Hills florists offer same-day delivery?</h3>
                  {openFaq === 0 ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                <div id="faq-answer-0" className={openFaq === 0 ? 'block' : 'hidden'}>
                  <p className="mt-3 leading-7 text-black px-2">
                    Yes, many Surry Hills-based florists offer same-day delivery when orders are placed before 2pm on weekdays.
                  </p>
                </div>
              </div>

              <div className="border-b pb-4">
                <button
                  onClick={() => setOpenFaq(openFaq === 1 ? null : 1)}
                  className="flex justify-between items-center w-full text-left p-2 hover:bg-gray-50 rounded transition-colors min-h-[48px]"
                  aria-expanded={openFaq === 1}
                  aria-controls="faq-answer-1"
                >
                  <h3 className="text-xl font-semibold text-black">How much does flower delivery in Surry Hills cost?</h3>
                  {openFaq === 1 ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                <div id="faq-answer-1" className={openFaq === 1 ? 'block' : 'hidden'}>
                  <p className="mt-3 leading-7 text-black px-2">
                    Delivery costs typically range from $7 to $16, depending on distance, florist, and delivery time.
                  </p>
                </div>
              </div>

              <div className="border-b pb-4">
                <button
                  onClick={() => setOpenFaq(openFaq === 2 ? null : 2)}
                  className="flex justify-between items-center w-full text-left p-2 hover:bg-gray-50 rounded transition-colors min-h-[48px]"
                  aria-expanded={openFaq === 2}
                  aria-controls="faq-answer-2"
                >
                  <h3 className="text-xl font-semibold text-black">What areas do Surry Hills florists deliver to?</h3>
                  {openFaq === 2 ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                <div id="faq-answer-2" className={openFaq === 2 ? 'block' : 'hidden'}>
                  <p className="mt-3 leading-7 text-black px-2">
                    Most florists in Surry Hills deliver to surrounding suburbs including Darlinghurst, Redfern, Chippendale, and Paddington.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Final CTA */}
          <div className="bg-gradient-to-r from-[brand-primary] to-[#E63E1C] text-white rounded-lg p-8 text-center md:relative sticky bottom-0 z-50">
            <h2 className="scroll-m-20 text-3xl font-semibold tracking-tight text-white mb-4">Ready to Order Beautiful Flowers?</h2>
            <p className="text-xl leading-7 text-white mb-6 opacity-90">
              Browse Surry Hills' premier florists offering same-day delivery on artistic arrangements
            </p>
            <Link 
              to={`${surryHillsSearchUrl}&utm_source=seo&utm_medium=landing&utm_campaign=surry-hills-florists`}
              className="bg-white text-brand-primary px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors inline-flex items-center gap-3 text-lg min-h-[48px]"
            >
              Shop Surry Hills Florists Now
              <ArrowRight className="w-6 h-6" />
            </Link>
          </div>

          {/* Related Articles */}
          <div className="mt-12">
            <h3 className="scroll-m-20 text-2xl font-semibold tracking-tight text-black mb-6">Related Articles</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <Link to="/blog/birthday-flowers-sydney" className="group">
                <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                  <h4 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2 group-hover:text-brand-primary transition-colors">
                    Birthday Flowers Sydney: Best Options for Every Age
                  </h4>
                  <p className="leading-7 text-black">Find the perfect birthday flowers for any celebration</p>
                </div>
              </Link>
              <Link to="/blog/local-vs-corporate-florists" className="group">
                <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                  <h4 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2 group-hover:text-brand-primary transition-colors">
                    Local vs Corporate Florists: Why Local Wins
                  </h4>
                  <p className="leading-7 text-black">Discover the benefits of choosing local Sydney florists</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
} 